"use client";

import React from "react";
import HeaderSection from "@/components/layout/HeaderSection";
import Footer from "@/components/layout/Footer";
import { LibraryList } from "./components/LibraryList";
import { EmptyLibraryState } from "./components/EmptyLibraryState";
import { useLibraryPageData } from "./hooks/useLibraryPageData";

/**
 * LibraryPage Component
 * Main component for the Library page that composes all sections
 * Follows the same pattern as NewsPage
 */
export default function LibraryPage() {
  const { articles, loading, error, meta, refreshData, loadMore } =
    useLibraryPageData();

  if (loading && articles.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header Section */}
        <HeaderSection
          title="Thư viện"
          subtitle="Kiến tạo Xanh toả sáng tâm hồn Việt"
          backgroundImage="/header_90ad.png"
          ariaLabel="Trang thư viện tài liệu KTX"
          height="h-[400px]"
          titleSize="large"
          textAlignment="left"
        />

        {/* Loading Content */}
        <div className="bg-gray-50 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Loading Header */}
            <div className="text-center mb-12">
              <div className="h-8 bg-gray-200 rounded w-64 mx-auto mb-4 animate-pulse" />
              <div className="h-4 bg-gray-200 rounded w-96 mx-auto animate-pulse" />
            </div>

            {/* Loading spacer (no categories on this screen) */}
            <div className="mb-4" />

            {/* Loading Library Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className="bg-white rounded-xl shadow-lg overflow-hidden animate-pulse"
                >
                  <div className="h-48 bg-gray-200" />
                  <div className="p-6">
                    <div className="h-4 bg-gray-200 rounded mb-2" />
                    <div className="h-6 bg-gray-200 rounded mb-2" />
                    <div className="h-4 bg-gray-200 rounded mb-4" />
                    <div className="flex justify-between">
                      <div className="h-4 bg-gray-200 rounded w-20" />
                      <div className="h-4 bg-gray-200 rounded w-16" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 ml-64">
        {/* Header Section */}
        <HeaderSection
          title="Thư viện"
          subtitle="Kiến tạo Xanh toả sáng tâm hồn Việt"
          backgroundImage="/header_90ad.png"
          ariaLabel="Trang thư viện tài liệu KTX"
          height="h-[400px]"
          titleSize="large"
          subtitleSize="large"
          overlayOpacity="medium"
          textAlignment="left"
          maxWidth="max-w-3xl"
        />

        <div className="bg-gray-50 py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <EmptyLibraryState
              title="Lỗi khi tải thư viện"
              description={error}
            />
            <div className="text-center mt-6">
              <button
                onClick={refreshData}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200"
              >
                Thử lại
              </button>
            </div>
          </div>
        </div>

        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <HeaderSection
        title="Thư viện"
        subtitle="Kiến tạo Xanh toả sáng tâm hồn Việt"
        backgroundImage="/header_90ad.png"
        ariaLabel="Trang thư viện tài liệu KTX"
        height="h-[400px]"
        titleSize="large"
        subtitleSize="large"
        overlayOpacity="medium"
        textAlignment="left"
        maxWidth="max-w-3xl"
      />

      {/* Main content area */}
      <div className="bg-gray-50">
        {/* Library List with filtering and pagination */}
        <LibraryList
          articles={articles}
          loading={loading}
          onLoadMore={meta && meta.page < meta.pageCount ? loadMore : undefined}
          hasMore={meta ? meta.page < meta.pageCount : false}
        />
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
}
